/**
 * Virtual tour links are rendered in an <iframe> on the public room page, but
 * the links people paste are usually the "watch" form of a video, which YouTube
 * (and Vimeo) refuse to frame — the box just stays blank. Only their embed
 * URLs work, so recognised YouTube and Vimeo video links are rewritten to that
 * form. Everything else (Matterport, Kuula, an unrecognised YouTube/Vimeo page)
 * passes through unchanged — it is upgraded to https but not checked for
 * whether the provider allows framing.
 */

const YOUTUBE_ID = /^[\w-]{11}$/;
const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "youtube-nocookie.com",
  "youtu.be",
]);

function stripSubdomain(host: string): string {
  return host.toLowerCase().replace(/^(www|m|music)\./, "");
}

/** `90`, `90s` and `1h2m30s` (the forms YouTube's share button produces) → seconds. */
function parseStartSeconds(raw: string | null): number | null {
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return Number(raw);
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw);
  if (!m || !(m[1] || m[2] || m[3])) return null;
  return Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
}

function youtubeEmbed(url: URL): string | null {
  const host = stripSubdomain(url.hostname);
  const segments = url.pathname.split("/").filter(Boolean);

  let id: string | undefined;
  if (host === "youtu.be") {
    id = segments[0];
  } else if (segments[0] === "watch") {
    id = url.searchParams.get("v") ?? undefined;
  } else if (["shorts", "live", "embed", "v"].includes(segments[0] ?? "")) {
    id = segments[1];
  }

  if (id && YOUTUBE_ID.test(id)) {
    const start = parseStartSeconds(
      url.searchParams.get("start") ?? url.searchParams.get("t")
    );
    return `https://www.youtube.com/embed/${id}${start ? `?start=${start}` : ""}`;
  }

  const list = url.searchParams.get("list");
  if ((segments[0] === "playlist" || segments[0] === "watch") && list) {
    return `https://www.youtube.com/embed/videoseries?list=${encodeURIComponent(list)}`;
  }
  return null;
}

function vimeoEmbed(url: URL): string | null {
  const segments = url.pathname.split("/").filter(Boolean);
  // vimeo.com/showcase/<albumId>/video/<videoId>: the video id follows "video",
  // and a bare showcase/album link has no single video to embed.
  const afterVideo = segments.findIndex((s) => s === "video" || s === "videos");
  const from = afterVideo === -1 ? 0 : afterVideo + 1;
  if (afterVideo === -1 && ["showcase", "album"].includes(segments[0] ?? "")) {
    return null;
  }
  const at = segments.findIndex((s, i) => i >= from && /^\d+$/.test(s));
  if (at === -1) return null;
  // Unlisted videos carry a privacy hash: vimeo.com/<id>/<hash> or player …?h=<hash>.
  const hash =
    url.searchParams.get("h") ??
    (/^[0-9a-f]+$/i.test(segments[at + 1] ?? "") ? segments[at + 1] : null);
  return `https://player.vimeo.com/video/${segments[at]}${hash ? `?h=${hash}` : ""}`;
}

/**
 * Rewrites a pasted tour link into one that works in an iframe. Input that is
 * empty or cannot be parsed as a URL comes back trimmed and otherwise untouched.
 */
export function normalizeVirtualTourUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";

  // "youtu.be/abc" pasted without a scheme.
  const withScheme = /^[a-z][a-z\d+.-]*:/i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/\//, "")}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return trimmed;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return trimmed;

  const host = stripSubdomain(url.hostname);
  if (YOUTUBE_HOSTS.has(host)) {
    const embed = youtubeEmbed(url);
    if (embed) return embed;
  } else if (host === "vimeo.com" || host === "player.vimeo.com") {
    const embed = vimeoEmbed(url);
    if (embed) return embed;
  }

  // Mixed content: an http:// frame is blocked on the https site. Also
  // canonicalizes an upper-case scheme, which the https check at render
  // time would otherwise reject.
  return withScheme.replace(/^https?:\/\//i, "https://");
}

/**
 * The `src` for the public iframe: the normalized link, or null when it is not
 * an https URL — a stored `javascript:` value must never reach an iframe.
 */
export function getVirtualTourEmbedSrc(
  raw: string | null | undefined
): string | null {
  const url = normalizeVirtualTourUrl(raw ?? "");
  return url.startsWith("https://") ? url : null;
}
